import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import axios from '../config/axiosConfig';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { loginUser, updateRestaurantDetails } from '../store/slices/authSlice';
import Toast from './Toast';

const PasswordReset = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
        currentPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    // Get user data from localStorage (set during login)
    const storedUserData = localStorage.getItem('userData');
    const userData = storedUserData ? JSON.parse(storedUserData) : {};
    const token = localStorage.getItem('authToken');
    const restaurantId = searchParams.get('restaurantId') || userData.restaurantId;
    const isFirstLogin = userData.isFirstLogin || searchParams.get('firstLogin') === 'true';
    
    // Redirect to login if no user data and no email in URL
    const emailFromUrl = searchParams.get('email');
    
    useEffect(() => {
        if (!userData.email && !emailFromUrl && !token) {
            navigate('/login');
        }
    }, []);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);

        try {
            const email = userData.email || emailFromUrl;
            if (!email) {
                setError('Email not found. Please login again.');
                setTimeout(() => navigate('/login'), 2000);
                return;
            }

            // Call password reset API
            await axios.post(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
                email: email,
                newPassword: formData.newPassword,
                currentPassword: isFirstLogin ? undefined : formData.currentPassword
            });

            showToast('Password reset successfully! Logging you in...');

            // After password reset, login again with new password
            setTimeout(async () => {
                try {
                    const email = userData.email || emailFromUrl;
                    const loginResponse = await axios.post(API_ENDPOINTS.AUTH_LOGIN, {
                        email: email,
                        password: formData.newPassword
                    });

                    if (loginResponse.data.user && loginResponse.data.token) {
                        // Store auth data
                        localStorage.setItem('authToken', loginResponse.data.token);
                        localStorage.setItem('userData', JSON.stringify(loginResponse.data.user));
                        
                        if (loginResponse.data.restaurant) {
                            localStorage.setItem('restaurantData', JSON.stringify(loginResponse.data.restaurant));
                            dispatch(updateRestaurantDetails(loginResponse.data.restaurant));
                        } else if (restaurantId) {
                            // Fetch restaurant data if not provided
                            try {
                                const restaurantResponse = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId));
                                if (restaurantResponse.data) {
                                    localStorage.setItem('restaurantData', JSON.stringify(restaurantResponse.data));
                                    dispatch(updateRestaurantDetails(restaurantResponse.data));
                                }
                            } catch (err) {
                                console.error('Failed to fetch restaurant data:', err);
                            }
                        }

                        // Dispatch login action
                        await dispatch(loginUser({
                            email: email,
                            password: formData.newPassword
                        }));

                        // Redirect based on role
                        if (loginResponse.data.user.role === 'restaurantadmin') {
                            navigate('/restaurant');
                        } else if (loginResponse.data.user.role === 'staff') {
                            // Check staff role name to redirect to appropriate dashboard
                            const roleName = loginResponse.data.user.roleName || loginResponse.data.user.role?.name;
                            if (roleName === 'COOK' || roleName === 'Cook' || roleName === 'cook') {
                                navigate('/chef');
                            } else {
                                navigate('/waiter');
                            }
                        } else if (loginResponse.data.user.role === 'companyemployee') {
                            navigate('/employee/dashboard');
                        } else {
                            navigate('/');
                        }
                    }
                } catch (loginError) {
                    console.error('Auto-login after password reset failed:', loginError);
                    showToast('Password reset successful. Please login again.', 'info');
                    navigate('/login');
                }
            }, 1000);
        } catch (error) {
            console.error('Password reset failed:', error);
            setError(error.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <img src="/logo-bright.svg" alt="RestroAI" className="h-16 w-auto mx-auto mb-6" />
                    <h2 className="text-3xl font-bold text-white mb-2">
                        {isFirstLogin ? 'Set Your Password' : 'Reset Password'}
                    </h2>
                    <p className="text-white/70">
                        {isFirstLogin 
                            ? 'Please set a new password for your account' 
                            : 'Please enter your new password'}
                    </p>
                </div>

                {/* Password Reset Form */}
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {!isFirstLogin && (
                            <div>
                                <label htmlFor="currentPassword" className="block text-sm font-semibold text-white mb-2">
                                    Current Password
                                </label>
                                <input
                                    id="currentPassword"
                                    type="password"
                                    name="currentPassword"
                                    value={formData.currentPassword}
                                    onChange={handleInputChange}
                                    placeholder="Enter your current password"
                                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    required={!isFirstLogin}
                                />
                            </div>
                        )}

                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-semibold text-white mb-2">
                                New Password
                            </label>
                            <input
                                id="newPassword"
                                type="password"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                placeholder="Enter your new password (min. 6 characters)"
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                                minLength={6}
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-white mb-2">
                                Confirm New Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm your new password"
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                                minLength={6}
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
                                <div className="flex items-center space-x-2">
                                    <span className="text-red-400">⚠️</span>
                                    <span className="text-red-200 text-sm">{error}</span>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    {isFirstLogin ? 'Setting password...' : 'Resetting password...'}
                                </div>
                            ) : (
                                isFirstLogin ? 'Set Password' : 'Reset Password'
                            )}
                        </button>
                    </form>
                </div>

                {/* Back to Login */}
                <div className="text-center mt-6">
                    <button
                        onClick={() => navigate('/login')}
                        className="text-white/50 hover:text-white/70 text-sm transition-colors"
                    >
                        ← Back to Login
                    </button>
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default PasswordReset;

